import { useState, FormEvent } from 'react';
import { TextField, Button, Typography, Alert, CircularProgress, Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';
import { AuthLayout } from '@/components/AuthLayout';
import { sendPasswordResetDirect } from '@timetable/api';

type PageState = 'form' | 'sent';

export function ForgotPasswordPage() {
  const [pageState, setPageState] = useState<PageState>('form');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      console.log('Attempting to send password reset for:', email);
      await sendPasswordResetDirect(email);
      console.log('Password reset email sent successfully');
      setPageState('sent');
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.message ?? 'Failed to send reset email. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (pageState === 'sent') {
    return (
      <AuthLayout>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>Check your email</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          We sent a password reset link to <strong>{email}</strong>
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          If you don't see the email in your inbox, check your spam folder. The reset link expires in 1 hour.
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          <MuiLink component={Link} to="/login" underline="hover" color="primary">
            ← Back to sign in
          </MuiLink>
        </Typography>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>Forgot password?</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter your email address and we'll send you a link to reset your password.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Email</Typography>
        <TextField
          fullWidth
          size="small"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@school.edu"
          required
          autoComplete="email"
          autoFocus
          sx={{ mb: 3 }}
        />
        
        <Button 
          fullWidth 
          type="submit" 
          variant="contained" 
          size="large" 
          disabled={submitting}
        >
          {submitting ? <CircularProgress size={22} color="inherit" /> : 'Send reset link'}
        </Button>
      </form>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
        Remember your password?{' '}
        <MuiLink component={Link} to="/login" underline="hover" color="primary">
          Sign in
        </MuiLink>
      </Typography>
    </AuthLayout>
  );
}